import { useQueryClient } from '@tanstack/react-query';
import { ROLE_RANK, type Role, type UserView } from '@threadline/contracts';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, setAccessToken } from '@/lib/api';
import { connectSocket, disconnectSocket } from '@/lib/socket';

interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: UserView;
}

interface AuthValue {
  user: UserView | null;
  /** True until the boot-time refresh attempt has settled. */
  isLoading: boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (min: Role) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserView | null>(null);
  const [isLoading, setLoading] = useState(true);

  // On boot we hold an httpOnly refresh cookie and nothing else. One refresh
  // call tells us whether this browser still has a session.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token = await api.refresh();
      if (cancelled) return;

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const me = await api.get<UserView>('/auth/me');
        if (cancelled) return;
        setUser(me);
        connectSocket();
      } catch {
        setAccessToken(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);
  const client = useQueryClient();

  const login = useCallback(
    async (email: string, password: string, totp?: string) => {
      const result = await api.post<LoginResponse>('/auth/login', { email, password, totp });

      // Everything cached was fetched as whoever was signed in a moment ago -
      // their sessions, their chats, and the permissions the sidebar is drawn
      // from. Dropped before the new identity lands so nothing is rendered from
      // the last one.
      client.clear();

      setAccessToken(result.accessToken);
      setUser(result.user);
      connectSocket();
    },
    [client],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // Even if the call fails the local session is over; leaving the user on a
      // half-authenticated screen is worse than a stale server-side token.
      setAccessToken(null);
      setUser(null);
      disconnectSocket();
      client.clear();
    }
  }, [client]);

  const refreshUser = useCallback(async () => {
    setUser(await api.get<UserView>('/auth/me'));
  }, []);

  const can = useCallback(
    (min: Role) => (user ? ROLE_RANK[user.role] >= ROLE_RANK[min] : false),
    [user],
  );

  const value = useMemo<AuthValue>(
    () => ({ user, isLoading, login, logout, refreshUser, can }),
    [user, isLoading, login, logout, refreshUser, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/** Hides children when the signed-in user's role is below `min`. */
export function RequireRole({
  min,
  children,
  fallback = null,
}: {
  min: Role;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { can } = useAuth();
  return <>{can(min) ? children : fallback}</>;
}
